
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface BookingPaymentDetails {
  booking_coupons: Array<{
    id: string;
    discount_amount: number;
    user_coupon: {
      coupon: {
        name: string;
        code: string;
        discount_type: string;
        discount_value: number;
      };
    };
  }>;
  membership_info: {
    id: string;
    membership: {
      name: string;
      membership_type: string;
    } | null;
  } | null;
}

export const useBookingPaymentDetails = (bookingId?: string) => {
  return useQuery({
    queryKey: ['booking-payment-details', bookingId],
    queryFn: async () => {
      if (!bookingId) return null;

      console.log('Fetching payment details for booking:', bookingId);

      // Fetch booking coupons
      const { data: bookingCoupons, error: couponsError } = await supabase
        .from('booking_coupons')
        .select(`
          id,
          discount_amount,
          user_coupon_id,
          user_coupon:user_coupons(
            id,
            coupon_id,
            coupon:coupons(
              id,
              name,
              code,
              discount_type,
              discount_value
            )
          )
        `)
        .eq('booking_id', bookingId);

      console.log('Booking coupons query result:', {
        data: bookingCoupons,
        error: couponsError,
        count: bookingCoupons?.length || 0
      });

      if (couponsError) {
        console.error('Error fetching booking coupons:', couponsError);
      }

      // Get the booking details to check for membership_id
      const { data: booking, error: bookingError } = await supabase
        .from('bookings')
        .select('membership_id')
        .eq('id', bookingId)
        .single();

      let membershipInfo = null;
      
      if (booking && !bookingError && booking.membership_id) {
        console.log('Booking has direct membership_id reference:', booking.membership_id);
        
        // Get the specific membership that was used
        const { data: specificMembership, error: membershipError } = await supabase
          .from('user_memberships')
          .select(`
            id,
            membership:memberships!inner(
              id,
              name,
              membership_type,
              session_count
            )
          `)
          .eq('id', booking.membership_id)
          .single();

        if (specificMembership && !membershipError) {
          membershipInfo = {
            id: specificMembership.id,
            membership: specificMembership.membership
          };
          console.log('Found specific membership used for booking:', membershipInfo);
        } else {
          console.log('Could not find specific membership:', membershipError);
        }
      } else {
        console.log('No membership_id found in booking - not showing membership info');
      }

      console.log('Final payment details result:', {
        bookingCoupons: bookingCoupons || [],
        membershipInfo: membershipInfo || null,
        bookingCouponsCount: bookingCoupons?.length || 0
      });

      return {
        booking_coupons: bookingCoupons || [],
        membership_info: membershipInfo || null
      } as BookingPaymentDetails;
    },
    enabled: !!bookingId,
  });
};
